export const baseStyles = `
/* Host element MUST be block so the shadow content takes width.
   Custom elements default to display: inline which collapses the layout.
   NO layout/paint containment here: it would make the host the containing
   block for every position: fixed descendant, so booking modals would centre
   inside the widget's own box instead of the viewport. On a tall widget (e.g.
   a retreat page with lots of text) the dialog then lands thousands of px
   off-screen while body scroll is locked — the page just goes grey. */
/* Stacked widgets used to sit flush against each other: a page builder emits the
   custom elements back to back, and blank lines in the HTML do nothing, so an
   integrator had no way to separate them without wrapping every tag by hand.
   The gap is a variable so a site that wants them flush can set
   --hsw-widget-gap: 0 on the element (or on a parent). */
:host {
  display: block;
  width: 100%;
  contain: style;
  margin-bottom: var(--hsw-widget-gap, 2.5rem);
}

/* Reset and base styles for yoga widgets */
*, *::before, *::after {
  box-sizing: border-box;
}

.yoga-widget {
  font-family: var(--yoga-font, 'Inter', system-ui, sans-serif);
  font-size: 16px;
  line-height: 1.5;
  color: var(--yoga-text, #111827);
  background: transparent;
  /* The theme-driven vars (--yoga-primary, --yoga-text, --yoga-border) are set
     on :host by generateThemeStyles() from the tenant's branding + mode. Do NOT
     redeclare them here: .yoga-widget is a descendant of :host, so a value set
     here wins for all widget content and silently overrides the studio's brand
     colour (this is why every widget rendered orange) and dark mode. Only vars
     the theme layer does not provide live here. --yoga-radius here and per
     template below is only the default: theme-radius overrides it from
     generateThemeStyles() (core/theme.ts). */
  --yoga-text-muted: #737373;
  --yoga-radius: 0;
}

/* Studio template — clean, minimal, sharp corners */
.yoga-widget[data-template="studio"] {
  --yoga-radius: 0;
  --yoga-card-bg: #ffffff;
  --yoga-card-border: #e5e7eb;
}

/* Fighter template — dark, bold */
.yoga-widget[data-template="fighter"], .yoga-widget[data-template="power"] {
  --yoga-card-bg: #1a1a1a;
  --yoga-card-border: rgba(255,255,255,0.1);
  --yoga-text: #f5f5f5;
  --yoga-text-muted: #a3a3a3;
}

/* Wellness — warm tones */
.yoga-widget[data-template="wellness"] {
  --yoga-radius: 12px;
  --yoga-card-bg: #fefcf8;
  --yoga-card-border: #e8e0d0;
}

/* Holistic — earthy/sage */
.yoga-widget[data-template="holistic"] {
  --yoga-radius: 16px;
  --yoga-card-bg: #fafaf5;
  --yoga-card-border: #d4d4c4;
}

/* Flow — gradient feel */
.yoga-widget[data-template="flow"] {
  --yoga-radius: 16px;
  --yoga-card-bg: #ffffff;
  --yoga-card-border: rgba(255,107,167,0.2);
}

/* Zen — rolige, bløde runde hjørner (yoga/pilates/wellness). Matcher
   lib/template-config.ts: borderRadius 'rounded', mood 'calm'. Uden denne
   blok faldt zen (som er tenant-DEFAULT) tilbage til skarpe hjørner. */
.yoga-widget[data-template="zen"] {
  --yoga-radius: 12px;
  --yoga-card-bg: #ffffff;
  --yoga-card-border: #e9e7f3;
}

/* Soft — luftigt, dæmpede jordfarver (retreats/fordybelse). rounded/calm. */
.yoga-widget[data-template="soft"] {
  --yoga-radius: 16px;
  --yoga-card-bg: #faf8f3;
  --yoga-card-border: #e7e2d6;
}

/* Education/Undervisning — rent og indbydende (musik-/sprogskoler). Native
   styling er 'sharp' + 'bold', så skarpe hjørner som studio. */
.yoga-widget[data-template="education"] {
  --yoga-radius: 0;
  --yoga-card-bg: #ffffff;
  --yoga-card-border: #e5e7eb;
}

/* Typography */
h1, h2, h3, h4, h5, h6 {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  line-height: 1.25;
}

h1 { font-size: 1.875rem; }
h2 { font-size: 1.5rem; }
h3 { font-size: 1.25rem; }
h4 { font-size: 1.125rem; }

p {
  margin: 0 0 1rem 0;
}

/* Buttons */
.yoga-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  border: none;
  border-radius: var(--yoga-radius, 0.5rem);
  cursor: pointer;
  transition: all 0.15s ease;
}

.yoga-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.yoga-btn-primary {
  background: var(--yoga-primary, #10b981);
  color: white;
}

.yoga-btn-primary:hover:not(:disabled) {
  background: var(--yoga-primary-600, #059669);
}

.yoga-btn-secondary {
  background: var(--yoga-bg-secondary, #f3f4f6);
  color: var(--yoga-text, #111827);
}

.yoga-btn-secondary:hover:not(:disabled) {
  background: var(--yoga-border, #e5e7eb);
}

.yoga-btn-outline {
  background: transparent;
  border: 1px solid var(--yoga-border, #e5e7eb);
  color: var(--yoga-text, #111827);
}

.yoga-btn-outline:hover:not(:disabled) {
  background: var(--yoga-bg-secondary, #f3f4f6);
}

/* Form elements */
.yoga-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--yoga-text, #111827);
  background: var(--yoga-bg, #ffffff);
  border: 1px solid var(--yoga-border, #e5e7eb);
  border-radius: var(--yoga-radius, 0.5rem);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.yoga-input:focus {
  outline: none;
  border-color: var(--yoga-primary, #10b981);
  box-shadow: 0 0 0 3px var(--yoga-primary-100, rgba(16, 185, 129, 0.1));
}

.yoga-input::placeholder {
  color: var(--yoga-text-secondary, #9ca3af);
}

.yoga-label {
  display: block;
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--yoga-text, #111827);
}

.yoga-error {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #dc2626;
}

/* Cards — match HS-native studio-card aesthetic */
.yoga-card {
  background: var(--yoga-card-bg, #ffffff);
  border: 1px solid var(--yoga-card-border, #e5e7eb);
  border-radius: var(--yoga-radius, 0);
  padding: 2rem;
  transition: all 0.3s ease;
}

.yoga-card:hover, .yoga-card-hover:hover {
  border-color: var(--yoga-text-muted, #d4d4d4);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* Grid system */
.yoga-grid {
  display: grid;
  gap: 1rem;
}

.yoga-grid-2 {
  grid-template-columns: repeat(2, 1fr);
}

.yoga-grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 640px) {
  .yoga-grid-2, .yoga-grid-3 {
    grid-template-columns: 1fr;
  }
}

/* Flex utilities */
.yoga-flex {
  display: flex;
}

.yoga-flex-col {
  flex-direction: column;
}

.yoga-items-center {
  align-items: center;
}

.yoga-justify-between {
  justify-content: space-between;
}

.yoga-justify-center {
  justify-content: center;
}

.yoga-gap-1 { gap: 0.25rem; }
.yoga-gap-2 { gap: 0.5rem; }
.yoga-gap-3 { gap: 0.75rem; }
.yoga-gap-4 { gap: 1rem; }

/* Spacing utilities */
.yoga-mt-2 { margin-top: 0.5rem; }
.yoga-mt-4 { margin-top: 1rem; }
.yoga-mb-2 { margin-bottom: 0.5rem; }
.yoga-mb-4 { margin-bottom: 1rem; }
.yoga-p-4 { padding: 1rem; }

/* Text utilities */
.yoga-text-sm { font-size: 0.875rem; }
.yoga-text-xs { font-size: 0.75rem; }
.yoga-text-muted { color: var(--yoga-text-secondary, #6b7280); }
.yoga-text-center { text-align: center; }
.yoga-font-medium { font-weight: 500; }
.yoga-font-semibold { font-weight: 600; }

/* Badge */
.yoga-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}

.yoga-badge-success {
  background: var(--yoga-primary-100, #d1fae5);
  color: var(--yoga-primary-800, #065f46);
}

.yoga-badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.yoga-badge-error {
  background: #fee2e2;
  color: #991b1b;
}

/* Loading spinner */
.yoga-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--yoga-border, #e5e7eb);
  border-top-color: var(--yoga-primary, #10b981);
  border-radius: 50%;
  animation: yoga-spin 0.8s linear infinite;
}

@keyframes yoga-spin {
  to { transform: rotate(360deg); }
}

/* Modal backdrop */
.yoga-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 50;
}

.yoga-modal {
  background: var(--yoga-bg, #ffffff);
  border-radius: calc(var(--yoga-radius, 0.5rem) * 2);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Empty state */
.yoga-empty {
  text-align: center;
  padding: 2rem;
  color: var(--yoga-text-secondary, #6b7280);
}

.yoga-empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}
`
